import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { ImagePlus, Loader2, Pencil, Plus, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FILTERS } from "@/data/products";
import { imageUrl } from "@/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = FILTERS.filter((f) => f.slug !== "todos");

const EMPTY_FORM = {
  name: "",
  category: "racoes",
  price: "",
  unit: "",
  badge: "",
  image: "",
  short: "",
  specs: "",
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiMode, setAiMode] = useState("product");
  const fileInput = useRef(null);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/products`, { withCredentials: true });
      setProducts(res.data);
    } catch {
      toast.error("Não foi possível carregar os produtos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setAiPrompt("");
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      category: p.category,
      price: p.price,
      unit: p.unit || "",
      badge: p.badge || "",
      image: p.image,
      short: p.short || "",
      specs: (p.specs || []).join("\n"),
    });
    setAiPrompt("");
  };

  const closeForm = () => {
    setEditing(null);
    setForm(null);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const res = await axios.post(`${API}/upload-image`, data, { withCredentials: true });
      setForm((f) => ({ ...f, image: res.data.url }));
      toast.success("Foto enviada!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha no envio da imagem.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleGenerate = async () => {
    if (!aiPrompt.trim()) return;
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/generate-image`,
        { prompt: aiPrompt.trim(), mode: aiMode },
        { withCredentials: true, timeout: 120000 }
      );
      setForm((f) => ({ ...f, image: res.data.url }));
      toast.success("Imagem gerada com IA!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "A IA não conseguiu gerar a imagem.");
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const categoryLabel = CATEGORIES.find((c) => c.slug === form.category)?.label || form.category;
    const payload = {
      name: form.name,
      category: form.category,
      categoryLabel,
      price: form.price,
      unit: form.unit,
      badge: form.badge || null,
      image: form.image,
      short: form.short,
      specs: form.specs.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing) {
        await axios.put(`${API}/products/${editing.id}`, payload, { withCredentials: true });
        toast.success("Produto atualizado!");
      } else {
        await axios.post(`${API}/products`, payload, { withCredentials: true });
        toast.success("Produto cadastrado!");
      }
      closeForm();
      loadProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível salvar o produto.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Remover "${p.name}" do catálogo?`)) return;
    try {
      await axios.delete(`${API}/products/${p.id}`, { withCredentials: true });
      toast.success("Produto removido.");
      loadProducts();
    } catch {
      toast.error("Não foi possível remover o produto.");
    }
  };

  const formOpen = form !== null;

  return (
    <section data-testid="admin-products-panel">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-moss">
          Cadastre produtos, envie fotos reais ou gere imagens com IA. Tudo aparece no catálogo do
          site na hora.
        </p>
        <button
          type="button"
          onClick={openNew}
          data-testid="admin-new-product-button"
          className="inline-flex items-center gap-2 rounded-full bg-olive px-5 py-2.5 text-sm font-semibold text-parchment transition-colors hover:bg-forest"
        >
          <Plus className="h-4 w-4" />
          Novo produto
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-olive" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p) => (
            <div
              key={p.id}
              data-testid={`admin-product-${p.id}`}
              className="flex gap-4 rounded-[1.5rem] border border-linen bg-white p-4 shadow-sm"
            >
              <img
                src={imageUrl(p.image)}
                alt={p.name}
                className="h-20 w-20 shrink-0 rounded-xl object-cover"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-ink">{p.name}</p>
                <p className="text-xs text-moss">
                  {p.categoryLabel} · {p.price}
                </p>
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={() => openEdit(p)}
                    data-testid={`admin-edit-${p.id}`}
                    className="inline-flex items-center gap-1 rounded-full border border-linen px-3 py-1.5 text-xs font-medium text-olive transition-colors hover:bg-linen/50"
                  >
                    <Pencil className="h-3 w-3" />
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(p)}
                    data-testid={`admin-delete-${p.id}`}
                    className="inline-flex items-center gap-1 rounded-full border border-clay/30 px-3 py-1.5 text-xs font-medium text-clay transition-colors hover:bg-clay/10"
                  >
                    <Trash2 className="h-3 w-3" />
                    Remover
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={formOpen} onOpenChange={(open) => !open && closeForm()}>
        <DialogContent
          data-testid="product-form-modal"
          className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-[2rem] border-linen bg-parchment p-8"
        >
          {form && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-medium text-ink">
                  {editing ? "Editar produto" : "Novo produto"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSave} className="mt-4 space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <label className="block sm:col-span-2">
                    <span className="mb-1.5 block text-sm font-medium text-ink">Nome do produto</span>
                    <input
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="product-form-name"
                      className="w-full rounded-xl border border-linen bg-white px-4 py-3 text-sm outline-none focus:border-clay"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-medium text-ink">Categoria</span>
                    <select
                      value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      data-testid="product-form-category"
                      className="w-full appearance-none rounded-xl border border-linen bg-white px-4 py-3 text-sm outline-none focus:border-clay"
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c.slug} value={c.slug}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-medium text-ink">Preço</span>
                    <input
                      required
                      value={form.price}
                      onChange={(e) => setForm({ ...form, price: e.target.value })}
                      placeholder="R$ 89,90 ou Consulte"
                      data-testid="product-form-price"
                      className="w-full rounded-xl border border-linen bg-white px-4 py-3 text-sm outline-none focus:border-clay"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-medium text-ink">Unidade</span>
                    <input
                      value={form.unit}
                      onChange={(e) => setForm({ ...form, unit: e.target.value })}
                      placeholder="saco 15kg"
                      data-testid="product-form-unit"
                      className="w-full rounded-xl border border-linen bg-white px-4 py-3 text-sm outline-none focus:border-clay"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-medium text-ink">Selo (opcional)</span>
                    <input
                      value={form.badge}
                      onChange={(e) => setForm({ ...form, badge: e.target.value })}
                      placeholder="Mais Vendido"
                      data-testid="product-form-badge"
                      className="w-full rounded-xl border border-linen bg-white px-4 py-3 text-sm outline-none focus:border-clay"
                    />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="mb-1.5 block text-sm font-medium text-ink">Descrição curta</span>
                    <textarea
                      rows={2}
                      value={form.short}
                      onChange={(e) => setForm({ ...form, short: e.target.value })}
                      data-testid="product-form-short"
                      className="w-full resize-none rounded-xl border border-linen bg-white px-4 py-3 text-sm outline-none focus:border-clay"
                    />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="mb-1.5 block text-sm font-medium text-ink">
                      Detalhes (um por linha)
                    </span>
                    <textarea
                      rows={3}
                      value={form.specs}
                      onChange={(e) => setForm({ ...form, specs: e.target.value })}
                      placeholder={"Saco de 15kg\nSem corantes"}
                      data-testid="product-form-specs"
                      className="w-full resize-none rounded-xl border border-linen bg-white px-4 py-3 text-sm outline-none focus:border-clay"
                    />
                  </label>
                </div>

                <div className="rounded-2xl border border-linen bg-white p-5">
                  <p className="mb-3 text-sm font-medium text-ink">Imagem do produto</p>
                  <div className="flex flex-col gap-4 sm:flex-row">
                    <div className="flex h-40 w-full items-center justify-center overflow-hidden rounded-xl border border-dashed border-linen bg-parchment sm:w-40">
                      {form.image ? (
                        <img src={imageUrl(form.image)} alt="Prévia" className="h-full w-full object-cover" data-testid="product-form-image-preview" />
                      ) : (
                        <ImagePlus className="h-8 w-8 text-moss/40" />
                      )}
                    </div>
                    <div className="flex-1 space-y-3">
                      <input
                        ref={fileInput}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleUpload}
                        className="hidden"
                        data-testid="product-form-file-input"
                      />
                      <button
                        type="button"
                        onClick={() => fileInput.current?.click()}
                        disabled={uploading}
                        data-testid="product-form-upload-button"
                        className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-olive px-4 py-2.5 text-sm font-semibold text-olive transition-colors hover:bg-olive hover:text-parchment disabled:opacity-60"
                      >
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                        {uploading ? "Enviando..." : "Enviar foto do produto"}
                      </button>
                      <div className="flex gap-2">
                        <select
                          value={aiMode}
                          onChange={(e) => setAiMode(e.target.value)}
                          data-testid="product-form-ai-mode"
                          className="appearance-none rounded-xl border border-linen bg-parchment px-3 py-2.5 text-xs outline-none focus:border-clay"
                        >
                          <option value="product">Foto de produto</option>
                          <option value="banner">Banner promocional</option>
                        </select>
                        <input
                          value={aiPrompt}
                          onChange={(e) => setAiPrompt(e.target.value)}
                          placeholder="Descreva a imagem para a IA..."
                          data-testid="product-form-ai-prompt"
                          className="flex-1 rounded-xl border border-linen bg-parchment px-3 py-2.5 text-xs outline-none focus:border-clay"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleGenerate}
                        disabled={generating || !aiPrompt.trim()}
                        data-testid="product-form-ai-generate"
                        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-clay px-4 py-2.5 text-sm font-semibold text-parchment transition-colors hover:bg-olive disabled:opacity-60"
                      >
                        {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                        {generating ? "Gerando com IA... (leva até 1 min)" : "Gerar imagem com IA"}
                      </button>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={saving || !form.image}
                  data-testid="product-form-save"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-olive px-6 py-4 text-sm font-semibold text-parchment transition-colors hover:bg-forest disabled:opacity-60"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editing ? "Salvar alterações" : "Cadastrar produto"}
                </button>
                {!form.image && (
                  <p className="text-center text-xs text-moss">Envie ou gere uma imagem para salvar.</p>
                )}
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
